import { useBoolean } from '~/hooks/useBoolean';
import { CSSProperties, ReactNode } from 'react';
import { useCustomPopper } from '~/hooks/popper';
import ClickOutside from '../ClickOutside';
import { getFixedHeightModifier } from '~/utils/popper';
import { SectionsType } from './types';
import ContextMenu from './ContextMenu';
import { ThreeDotsVertical } from '@emotion-icons/bootstrap';
import * as Styles from './styles';

const FIXED_HEIGHT = getFixedHeightModifier(20);

interface IContextMenuEntryProps {
  sections: SectionsType;
  onAction?: (mode: 'open' | 'close') => void;
  trigger?: ReactNode;
  shouldCenter?: boolean;
  fixedStyles?: CSSProperties;
  isDisabled?: boolean;
}

const ContextMenuEntry = ({
  sections,
  onAction,
  trigger,
  shouldCenter = true,
  fixedStyles = {},
  isDisabled = false,
}: IContextMenuEntryProps) => {
  const [isDropdownOpen, dropdownOpenActions] = useBoolean();
  const popperAttributes = useCustomPopper({
    strategy: 'fixed',
    placement: 'bottom-end',
    modifiers: [
      {
        name: 'preventOverflow',
        options: {
          altBoundary: false,
          boundary: document.body,
        },
      },
      {
        name: 'offset',
        options: {
          offset: [0, 4],
        },
      },
      {
        name: 'flip',
        options: {
          altBoundary: false,
        },
      },
      FIXED_HEIGHT,
    ],
  });

  function handleClose() {
    dropdownOpenActions.off();
  }

  function handleClick() {
    if (isDisabled) {
      return;
    }

    if (isDropdownOpen) {
      onAction?.('close');
    } else {
      onAction?.('open');
    }
    dropdownOpenActions.toggle();
  }

  return (
    <Styles.ControlWrapper
      data-viewtype="walkthrough-element"
      role="presentation"
      className={`${isDisabled ? '' : 'cursor-pointer'} pos-r ${shouldCenter ? 'center' : ''}`}
      isActive={isDropdownOpen}
      onClick={handleClick}
      haveDynamicWidth={Boolean(trigger)}
      {...popperAttributes.reference}
    >
      {trigger ?? (
        <ThreeDotsVertical size={20} stroke={isDisabled ? 'var(--neut3)' : 'var(--t-black)'} />
      )}
      {isDropdownOpen && (
        <ClickOutside onClose={handleClose}>
          <ContextMenu
            sections={sections}
            onClick={handleClose}
            fixedAttributes={{
              ...popperAttributes.fixed,
              style: {
                ...popperAttributes.fixed.style,
                ...fixedStyles,
              },
            }}
          />
        </ClickOutside>
      )}
    </Styles.ControlWrapper>
  );
};

export default ContextMenuEntry;
